import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Tiles from './components/Tiles';
import Modal from '../../../core/components/Modal/Modal';
import classNames from 'classnames';
import styles from './CaseDetailsTiles.module.scss';
import ProvidersModal from './components/ProvidersModal';
import { Alert, Button, Loader, ModuleContainer } from '../../../core/components';
import { CaseDetailsType, emptyCaseDetailsType } from './types/CaseDetailsType';
import { InfoMessageService, StyleService, TranslationService } from '../../../core/services';
import ApiProvider from '../../../core/api/ApiProvider';
import { ButtonDataType } from './types/ButtonDataType';
import { PaymentProviderType } from '../../types/PaymentProviderType';
import { ResponseApiType } from '../../types/ResponseApiType';
import { ApplyForDelay, InstallmentPlan, CaseDispute } from '..';
import Status from '../../../core/types/Status';
import { RedirectContentResponse } from '../../types/RedirectContentResponse';
import styled, { css } from 'styled-components';
import { DictionaryType } from '../../../core/types/DictionaryType';
import { useAppContext } from '../../../AppContext';
import useEffectAsync from '../../../core/hooks/useEffectAsync';
import QueryParameter from '../../../core/utils/QueryParameter';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import NumberInput from '../../../core/components/NumberInput/NumberInput';

const CaseDetailsTile = () => {
  const { caseNumber, tenantUId } = useParams() as { caseNumber: string; tenantUId: string };
  const context = useAppContext();
  const whitelabelingStyles = StyleService.applyMany(
    ['module:paynow:firstColor', 'module:paynow:secondColor', 'module:paynow:payButton'],
    context.styles
  );

  const [caseNo, setCaseNo] = useState<string>('');
  const [caseDetailsType, setCaseDetailsType] = useState<CaseDetailsType>({
    ...emptyCaseDetailsType,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isTiles, setIsTilesLoading] = useState<boolean>(true);
  const [isOpenProvidersModal, setIsOpenProvidersModal] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [payAmount, setPayAmount] = useState<string | number>('');
  const [modalToRender, setModalToRender] = useState<{
    id?: string;
    component: any;
    className?: string;
    title?: string;
  }>();

  const validationSchema = Yup.object({
    payAmount: Yup.string().required().matches(/[1-9]/),
  });

  const fetchCaseDetailsTiles = async () => {
    try {
      const resultGetCaseDetailsTiles = await ApiProvider.get<ResponseApiType<CaseDetailsType>>(
        `CaseDetailsTilesModule/GetCaseDetailsTiles/${caseNumber}`
      );
      if (resultGetCaseDetailsTiles.success) {
        setCaseDetailsType(resultGetCaseDetailsTiles.data);

        setCaseNo(resultGetCaseDetailsTiles.data.payNowTile.caseNo);
      }
    } catch (err) {
    } finally {
      setIsTilesLoading(false);
      setIsLoading(false);
    }
  };

  const getPaymentUrl = async (provider: PaymentProviderType) => {
    return await ApiProvider.post<ResponseApiType<string>>(
      'CaseDetailsTilesModule/GetPaymentServiceUrl',
      {
        caseNo: caseNumber,
        payAmount: payAmount,
        providerType: provider,
      }
    );
  };

  const goToPaymentsPage = async (provider: PaymentProviderType) => {
    var response = await getPaymentUrl(provider);
    if (response.success) {
      var redirectResponse = await ApiProvider.get<Status<RedirectContentResponse>>(response.data);
      if (redirectResponse.data.method === 'GET') {
        window.location.href = redirectResponse.data.url;
      } else {
        const r = document.createRange();
        const f = r.createContextualFragment(redirectResponse.data.body);
        document.getElementsByTagName('body')[0].appendChild(f);
        document.querySelector<HTMLButtonElement>('#submit-button')!.click();
      }
    } else {
      InfoMessageService.error(response.message);
    }
    setIsOpenProvidersModal(false);
  };

  const requestInvoiceCopy = async () => {
    setIsTilesLoading(true);
    try {
      let result = await ApiProvider.get<ResponseApiType>(
        `InvoiceCopyModule/GetInvoiceCopySettings/${caseNumber}`
      );
      if (result.success) {
        if (result.data.existingInvoiceCopyRequest === true) {
          setModalToRender({
            component: (
              <Alert variant={Alert.Variants.Info} className="mb-2">
                {TranslationService.translate('InvoiceCopyAlreadyRequested')}
                <br />
              </Alert>
            ),
            className: '',
            title: TranslationService.translate('InvoiceCopyTab'),
          });
          setIsOpen(true);
        } else {
          try {
            result = await ApiProvider.post<ResponseApiType>(
              `InvoiceCopyModule/RequestInvoiceCopy/${caseNumber}`,
              {}
            );
            if (result.success) {
              InfoMessageService.success(result.message);
            } else {
              InfoMessageService.error(result.message);
            }
          } catch (err) {
            InfoMessageService.error(
              (err as string) || TranslationService.translate('ErrorSthWentWrong')
            );
          }
        }
      } else {
        InfoMessageService.error(result.message);
      }
    } catch (err) {
      InfoMessageService.error(
        (err as string) || TranslationService.translate('ErrorSthWentWrong')
      );
    } finally {
      setIsTilesLoading(false);
    }
  };

  const handleOnApplyClose = (isOpenInstallmentModal: boolean) => {
    if (isOpenInstallmentModal) {
      setModalToRender({
        component: <InstallmentPlan onClose={() => setIsOpen(false)} />,
        className: '',
        title: TranslationService.translate('InstallmentPlan'),
      });
    } else {
      setIsOpen(false);
    }
  };

  const handleButtonClick = (button: ButtonDataType) => {
    switch (button.name) {
      case 'ApplyForDelay':
        setModalToRender({
          component: <ApplyForDelay onClose={handleOnApplyClose} />,
          className: '',
          title: TranslationService.translate('ApplyForDelay'),
        });
        setIsOpen(true);
        break;
      case 'InvoiceCopyRequest':
        requestInvoiceCopy();
        break;
      case 'DownloadInvoiceCopy':
        try {
          ApiProvider.downloadFile(`CaseDetailsTilesModule/DownloadInvoiceDocument/${caseNumber}`);
        } catch (error) {
          InfoMessageService.error(TranslationService.translate('FileNotFound'));
        }
        break;
      case 'Installments':
        setModalToRender({
          component: <InstallmentPlan onClose={() => setIsOpen(false)} />,
          className: styles.installmentsModal,
          title: TranslationService.translate('InstallmentPlan'),
        });
        setIsOpen(true);
        break;
      case 'CaseDispute':
        setModalToRender({
          component: <CaseDispute onClose={() => setIsOpen(false)} />,
          className: '',
          title: TranslationService.translate('CaseDispute'),
        });
        setIsOpen(true);
        break;
    }
  };

  const onToggle = () => {
    setIsOpen(false);
  };

  const parseSaldo = (saldo: string) => parseFloat(saldo.replace(',', '.').replace(/\s/g, ''));

  const getPayAllTileLabel = () => {
    if (caseDetailsType.payNowTile.isClosed) {
      return TranslationService.translate('CaseIsClosed');
    } else if (
      !caseDetailsType.payNowTile.isPaymentsFuncTurnOn ||
      parseSaldo(caseDetailsType.payNowTile.saldo) <= 0
    ) {
      return '';
    } else if (caseDetailsType.payNowTile.isPaid) {
      return TranslationService.translate('PaymentIsPending');
    } else {
      return TranslationService.translate('PayNow');
    }
  };

  const isPayButtonEnabled = () => {
    return (
      parseSaldo(caseDetailsType.payNowTile.saldo) > 0 &&
      caseDetailsType.payNowTile.isPaymentsFuncTurnOn &&
      !caseDetailsType.payNowTile.isPaid &&
      !caseDetailsType.payNowTile.isClosed
    );
  };

  const isPartialPaymentEnabled = () => {
    return caseDetailsType.payNowTile.enablePartialPayment;
  };

  useEffectAsync(async () => {
    await fetchCaseDetailsTiles();

    const paramIsInstallment = QueryParameter.getParameterByName('isInstallment');

    if (paramIsInstallment === 'true') {
      setModalToRender({
        component: <InstallmentPlan onClose={() => setIsOpen(false)} />,
        className: styles.installmentsModal,
        title: TranslationService.translate('InstallmentPlan'),
      });
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {modalToRender && (
        <Modal
          id={'close'}
          isOpen={isOpen}
          onToggle={onToggle}
          className={classNames(styles.modalWrapper, modalToRender && modalToRender.className)}
          closeBtnClass={classNames('modal-close-btn')}
          aria-label={TranslationService.translate('Close')}
        >
          <ModuleContainer noMargin={true} variant={ModuleContainer.ModuleVariant.Box}>
            <ModuleContainer.Header>
              {modalToRender.title && modalToRender.title}
            </ModuleContainer.Header>
            <ModuleContainer.Body>{modalToRender && modalToRender.component}</ModuleContainer.Body>
          </ModuleContainer>
        </Modal>
      )}

      <div className={styles.pageTitle}>
        <div className="container">
          <h1 data-cy="case-number">
            {TranslationService.translate('CaseNo')}: {caseNo}
          </h1>
        </div>
      </div>

      <div className="container">
        <ModuleContainer variant={ModuleContainer.ModuleVariant.Box}>
          {isLoading && <Loader opacity={0.4} />}
          <div className="row">
            <div className="col-12 col-md-8">
              <ModuleContainer noMargin={true}>
                <div className={classNames(styles.payment, styles.info)}>
                  <p className={styles.paymentName}>
                    {TranslationService.translate('PayNowTile_OriginalClaim')}
                  </p>
                  <p className={styles.paymentAmount}>
                    {caseDetailsType.payNowTile.originalClaim}
                    <span>{caseDetailsType.payNowTile.currency}</span>
                  </p>
                  {caseDetailsType.payNowTile.originalCurrency && (
                    <p className={styles.paymentOriginalCurrencyAmount}>
                      {caseDetailsType.payNowTile.originalCurrencyClaim}
                      <span>{caseDetailsType.payNowTile.originalCurrency}</span>
                    </p>
                  )}
                </div>
                <div className={classNames(styles.payment, styles.info)}>
                  <p className={styles.paymentName}>
                    {TranslationService.translate('PayNowTile_CaseCosts')}
                  </p>

                  <p className={styles.paymentAmount}>
                    {caseDetailsType.payNowTile.caseCosts}
                    <span>{caseDetailsType.payNowTile.currency}</span>
                  </p>
                </div>
                <div className={classNames(styles.payment, styles.info)}>
                  <p className={styles.paymentName}>
                    {TranslationService.translate('PayNowTile_Paid')}
                  </p>

                  <p className={styles.paymentAmount}>
                    {caseDetailsType.payNowTile.paid}
                    <span>{caseDetailsType.payNowTile.currency}</span>
                  </p>
                </div>
              </ModuleContainer>
            </div>

            <div className="col-12 col-md-4">
              <PayAllTileDiv styles={whitelabelingStyles}>
                <ModuleContainer
                  noMargin={true}
                  classNames={classNames(styles.payment, styles.payNow)}
                >
                  <div className={classNames(styles.paymentWrapper)}>
                    <div className={styles.row}>
                      <div className={styles.cell}>
                        <p className={styles.paymentName}>
                          {TranslationService.translate('PayNowTile_DebtBalance')}
                        </p>
                        <p className={styles.paymentAmount}>
                          {parseSaldo(caseDetailsType.payNowTile.saldo) < 0
                            ? 0
                            : caseDetailsType.payNowTile.saldo}
                          <span>{caseDetailsType.payNowTile.currency}</span>
                        </p>
                      </div>
                    </div>
                    <div className={classNames(styles.row)}>
                      <Formik
                        initialValues={{
                          payAmount:
                            parseSaldo(caseDetailsType.payNowTile.saldo) < 0
                              ? 0
                              : caseDetailsType.payNowTile.saldo,
                        }}
                        validationSchema={validationSchema}
                        onSubmit={(values) => {
                          if (isPayButtonEnabled()) {
                            setPayAmount(values.payAmount);
                            setIsOpenProvidersModal(true);
                          }
                        }}
                        enableReinitialize={true}
                      >
                        {() => (
                          <FormikForm className={styles.fullWidthForm}>
                            <div className={styles.row}>
                              {isPayButtonEnabled() && isPartialPaymentEnabled() && (
                                <div className={styles.cell}>
                                  <p className={styles.paymentName}>
                                    {TranslationService.translate('Amount')}
                                  </p>
                                  <NumberInput
                                    max={caseDetailsType.payNowTile.saldo}
                                    name="payAmount"
                                    dataCy={'PayAmount'}
                                    title={TranslationService.translate('PaymentAmount')}
                                  />
                                </div>
                              )}
                              {getPayAllTileLabel() !== '' && (
                                <div className={classNames(styles.cell, styles.last)}>
                                  <Button
                                    id="PayButton"
                                    type={Button.Types.Submit}
                                    variant={Button.Variants.Transparent}
                                    classNames={classNames(
                                      styles.paymentButton,
                                      (!isPayButtonEnabled() || !isPartialPaymentEnabled()) &&
                                        styles.disabledBtn
                                    )}
                                    disabled={!isPayButtonEnabled()}
                                    dataCy={'PayButton'}
                                  >
                                    {getPayAllTileLabel()}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </FormikForm>
                        )}
                      </Formik>
                    </div>
                  </div>
                </ModuleContainer>
              </PayAllTileDiv>
            </div>
          </div>
        </ModuleContainer>

        <ProvidersModal
          isOpen={isOpenProvidersModal}
          onToggle={setIsOpenProvidersModal}
          onPay={goToPaymentsPage}
        />

        <div className={styles.box}>
          <ModuleContainer>
            <ModuleContainer.Header title={TranslationService.translate('Summary')} headingLevel="h2" />

            <ModuleContainer.Body>
              {isTiles && <Loader opacity={0.4} />}

              <Tiles tiles={caseDetailsType.tiles} columns={caseDetailsType.tilesColumnsNum} />

              <div className="row mt-3">
                {caseDetailsType.buttons.map(
                  (b) =>
                    b.isVisible && (
                      <div key={b.name} className={classNames('col-auto pr-0', styles.tileButton)}>
                        <Button
                          id={`casedetailsbutton_${b.name}`}
                          type={Button.Types.Button}
                          variant={Button.Variants.Secondary}
                          disabled={b.isDisabled}
                          onClick={() => handleButtonClick(b)}
                        >
                          {b.displayName}
                        </Button>
                      </div>
                    )
                )}
              </div>
            </ModuleContainer.Body>
          </ModuleContainer>
        </div>
      </div>
    </>
  );
};

const PayAllTileDiv = styled.div<{
  styles: DictionaryType<DictionaryType<string>>;
}>`
  ${(props) => {
    let styles = '';

    if (props.styles['module:paynow:firstColor'].hasOwnProperty('backgroundColor')) {
      styles = `${styles} --module-paynow-tile-color-first: ${props.styles['module:paynow:firstColor'].backgroundColor};`;
    }

    if (props.styles['module:paynow:secondColor'].hasOwnProperty('backgroundColor')) {
      styles = `${styles} --module-paynow-tile-color-second: ${props.styles['module:paynow:secondColor'].backgroundColor};`;
    }

    if (props.styles['module:paynow:payButton'].hasOwnProperty('textColor')) {
      styles = `${styles} --module-paynow-tile-payButton-color: ${props.styles['module:paynow:payButton'].textColor};`;
    }

    if (props.styles['module:paynow:payButton'].hasOwnProperty('backgroundColor')) {
      styles = `${styles} --module-paynow-tile-payButton-bacground-color: ${props.styles['module:paynow:payButton'].backgroundColor};`;
    }

    if (props.styles['module:paynow:payButton'].hasOwnProperty('textColorHover')) {
      styles = `${styles} --module-paynow-tile-payButton-color-hover: ${props.styles['module:paynow:payButton'].textColorHover};`;
    }

    if (props.styles['module:paynow:payButton'].hasOwnProperty('backgroundColorHover')) {
      styles = `${styles} --module-paynow-tile-payButton-bacground-color-hover: ${props.styles['module:paynow:payButton'].backgroundColorHover};`;
    }

    return css`
      ${styles}
    `;
  }}
`;

export default CaseDetailsTile;
